/* eslint-disable @typescript-eslint/no-explicit-any */
import { create } from "zustand";
import { Subscription, TokenParsed } from "./auth";
import apiInputService from "services/apiInputService";
import { useCallback, useMemo } from "react";
import { Client } from "enums/Clients";

type AuthStore = {
  userInfo: any;
  setUserInfo: (userInfo: any) => void;

  tokenParsed: TokenParsed | undefined;
  setTokenParsed: (tokenParsed: any) => void;

  subscription: Subscription | undefined;
  setSubscription: (index: number) => void;

  subscriptionsAvailable: Subscription[];
  setSubscriptionsAvailable: (subscriptions: Subscription[]) => void;
};

export const useAuthStore = create<AuthStore>((set, get) => ({
  userInfo: undefined,
  setUserInfo: (userInfo: any) => set({ userInfo }),

  tokenParsed: undefined,
  setTokenParsed: (tokenParsed: TokenParsed) => {
    set({ tokenParsed });
    get().setSubscriptionsAvailable(
      tokenParsed.subscriptionAttributes.map(
        (subscription) => JSON.parse(subscription) as Subscription,
      ),
    );
  },

  subscription: undefined,
  setSubscription: (index: number) => {
    const subscriptionsAvailable = get().subscriptionsAvailable;

    if (subscriptionsAvailable.length < index) return;

    localStorage.setItem("subscriptionIndex", index.toString());
    const subscription = subscriptionsAvailable[index];
    apiInputService.defaults.params = {
      ...apiInputService.defaults.params,
      subscription: subscription?.subscriptionId,
    };
    localStorage.setItem("subscription", subscription?.subscriptionId);
    set({ subscription: subscriptionsAvailable[index] });
  },

  subscriptionsAvailable: [],
  setSubscriptionsAvailable: (subscriptions: Subscription[]) => {
    const subscriptionIndex: string =
      localStorage.getItem("subscriptionIndex") || "0";
    set({ subscriptionsAvailable: subscriptions });
    get().setSubscription(parseInt(subscriptionIndex));
  },
}));

export const useUserInfo = () => {
  const userInfo = useAuthStore((state) => state.userInfo);
  const setUserInfo = useAuthStore((state) => state.setUserInfo);

  return { userInfo, setUserInfo };
};

export const useTokenParsed = () => {
  const tokenParsed = useAuthStore((state) => state.tokenParsed);
  const setTokenParsed = useAuthStore((state) => state.setTokenParsed);

  return { tokenParsed, setTokenParsed };
};

export const useSubscription = () => {
  const [
    subscription,
    setSubscription,
    subscriptionsAvailable,
    setSubscriptionsAvailable,
  ] = useAuthStore((state) => [
    state.subscription,
    state.setSubscription,
    state.subscriptionsAvailable,
    state.setSubscriptionsAvailable,
  ]);

  const isCurrentSubscriptionFrom = useCallback(
    (client: Client) => {
      return (
        subscriptionsAvailable?.find((s) => s.mini === client)
          ?.subscriptionId === subscription?.subscriptionId
      );
    },
    [subscription, subscriptionsAvailable],
  );

  const isKuo = useMemo(() => isCurrentSubscriptionFrom(Client.GRUPO_KUO), [
    isCurrentSubscriptionFrom,
  ]);
  const isBeltrame = useMemo(() => isCurrentSubscriptionFrom(Client.BELTRAME), [
    isCurrentSubscriptionFrom,
  ]);
  const isElCorteIngles = useMemo(
    () => isCurrentSubscriptionFrom(Client.EL_CORTE_INGLES),
    [isCurrentSubscriptionFrom],
  );
  const isInfra = useMemo(() => isCurrentSubscriptionFrom(Client.INFRA), [
    isCurrentSubscriptionFrom,
  ]);
  const isAngloAmerican = useMemo(
    () => isCurrentSubscriptionFrom(Client.ANGLO_AMERICAN),
    [isCurrentSubscriptionFrom],
  );
  const isBanorte = useMemo(() => isCurrentSubscriptionFrom(Client.BANORTE), [
    isCurrentSubscriptionFrom,
  ]);
  const isAcelen = useMemo(() => isCurrentSubscriptionFrom(Client.ACELEN), [
    isCurrentSubscriptionFrom,
  ]);
  const isAngloAmericaBrazil = useMemo(
    () => isCurrentSubscriptionFrom(Client.ANGLO_AMERICAN_BRAZIL),
    [isCurrentSubscriptionFrom],
  );

  return {
    subscription,
    setSubscription,
    subscriptionsAvailable,
    setSubscriptionsAvailable,
    isKuo,
    isBeltrame,
    isElCorteIngles,
    isInfra,
    isAngloAmerican,
    isBanorte,
    isAcelen,
    isAngloAmericaBrazil,
  };
};
